import {
  TGetProductContractHandoffReqDto,
  TGetProductContractHandoffResDto,
} from "@/api/services/admin/product-contract/dtos/get-product-contract-handoff.dto";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { clientApi } from "@/utils/api";
import { CONTRACT_TOKEN_KEY } from "@/utils/constants";
import { createUrlWithParams } from "@/utils/url";
import { useQuery } from "@tanstack/react-query";
import Cookies from "js-cookie";

/**
 * Resolves the product contract handed off by the Center under `oauth:contract:<token>`.
 * The contract token is persisted by the OAuth callback into the `contract-token` cookie.
 */
export const getProductContractHandoffApi = async (req: TGetProductContractHandoffReqDto): Promise<TGetProductContractHandoffResDto> => {
  const url = createUrlWithParams("/product-contracts/handoff", req);
  const rs = await clientApi.get<TGetProductContractHandoffResDto>(url);
  return rs.data;
};

/**
 * Reads the handed-off contract for the current session. An explicit `token` overrides the cookie
 * (e.g. deep-linking with the token in hand).
 *
 * `isSigned` means the Center-side organization already has its match record, so the hand-off is
 * consumed and the create flow must stay closed. Unlike the RSO admin — which has one table per
 * organization type — Match only owns a single 紹介会社 record, so `organizationType` does not gate
 * creation here.
 */
export const useProductContractHandoff = (token?: string | null) => {
  const contractToken = token ?? Cookies.get(CONTRACT_TOKEN_KEY);

  const query = useQuery({
    queryKey: [QUERY_KEYS.PRODUCT_CONTRACT.GET_HANDOFF, contractToken],
    queryFn: () => {
      if (!contractToken) return null;
      return getProductContractHandoffApi({ token: contractToken as string });
    },
    enabled: Boolean(contractToken),
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
    staleTime: 0,
  });

  const isSigned: boolean | null | undefined = contractToken ? query.data?.data.isSigned : null;
  const contract = query.data?.data.data ?? null;

  const canCreateMatch = Boolean(contract) && !isSigned;

  return { ...query, isSigned, contract, canCreateMatch };
};

/** Drops the hand-off token once its contract has been consumed by a created match. */
export const clearContractToken = () => Cookies.remove(CONTRACT_TOKEN_KEY);
